import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useProxyParameters } from 'utils/api/ApiHooks';
import { ICONS } from '../../consts/consts';
import type { Me, Person } from '../../types';
import ConfirmAPI from '../../utils/api/ConfirmAPI';
import {
  ObjectiveWithRef as Objective,
  ObjectivePerson,
} from '../../utils/models/Objective';
import AvatarGroup from '../Widgets/People/AvatarGroup';
import ObjectiveCollaboratorsPopover from './ObjectiveCollaboratorsPopover';
import { DATE_PARAMETER } from './ObjectivesTimeFrameSelector';

interface Props {
  objective: Objective;
  onSave: (objective: Objective) => void;
  disabled?: boolean;
  minCollaborators?: number;
  isRootObjective: boolean;
  isIndividual: boolean;
  profilePerson?: Person;
  me?: Me;
}

const ObjectiveCollaborators: FC<Props> = ({
  objective,
  onSave,
  disabled,
  minCollaborators,
  isRootObjective,
  isIndividual,
  profilePerson,
  me,
}) => {
  const { formatMessage } = useIntl();
  const proxyParams = useProxyParameters();
  const [collaboratorsPopoverOpen, setCollaboratorsPopoverOpen] =
    useState(false);

  const toggle = useCallback(
    () => !disabled && setCollaboratorsPopoverOpen(!collaboratorsPopoverOpen),
    [disabled, collaboratorsPopoverOpen]
  );

  const onClose = useCallback(() => {
    setCollaboratorsPopoverOpen(false);
  }, []);

  // this caputres the case when the objective lose editability
  useEffect(() => {
    if (disabled) {
      setCollaboratorsPopoverOpen(false);
    }
  }, [disabled]);

  const allPeople = objective?.collaborators ?? [];

  const avatarsRef = useRef<HTMLButtonElement>(null);
  return (
    <>
      <button
        type="button"
        className="focusable-element"
        title={formatMessage({
          id: 'app.views.person.personal_objectives.collaborators.title',
          defaultMessage: 'Collaborators',
        })}
        ref={avatarsRef}
      >
        {allPeople.length > 0 ? (
          <AvatarGroup
            size="xs"
            className="d-none d-md-inline-block"
            people={allPeople.map((it) => ({
              ...it,
              onClick: () => {
                if (!disabled) {
                  setCollaboratorsPopoverOpen(true);
                }
              },
            }))}
            maxFaces={2}
            unlinked={!disabled}
            linkToSubPath={`/objectives?${DATE_PARAMETER}=${objective.coverage_start_date}#key=${objective.key}`}
            isExternalUrl={true}
          />
        ) : (
          !disabled && (
            <div
              className="btn btn-sm btn-rounded-circle btn-light"
              role="button"
              onClick={() => setCollaboratorsPopoverOpen(true)}
            >
              <i className={ICONS.ADD_PERSON} />
            </div>
          )
        )}
      </button>
      <ObjectiveCollaboratorsPopover
        target={avatarsRef}
        isOpen={collaboratorsPopoverOpen}
        onClose={onClose}
        toggle={toggle}
        minPeople={minCollaborators}
        initialValue={objective.collaborators}
        isRootObjective={isRootObjective}
        isIndividual={isIndividual}
        profilePerson={profilePerson}
        me={me}
        onSave={(collaborators: ObjectivePerson[]) => {
          ConfirmAPI.sendRequestToConfirm(
            'POST',
            `/objectives/${objective.key}/collaborators`,
            {
              ...proxyParams,
              collaborators: collaborators.map((it) => it.id),
            },
            (response, error) => {
              if (response) {
                // setLoading(false);
                // setItems(response.changes);
              }
              if (error) {
                // setLoading(false);
                // setError(true);
              }
            },
            null,
            null
          );
          onSave({
            ...objective,
            collaborators,
          });
        }}
      />
    </>
  );
};

export default ObjectiveCollaborators;
